import { CircleMarker, Marker, Polygon, Polyline, Tooltip } from "react-leaflet";
import L from "leaflet";

const centroid = (pts) => [pts.reduce((a, p) => a + p[0], 0) / pts.length, pts.reduce((a, p) => a + p[1], 0) / pts.length];
const labelIcon = (name) => L.divIcon({ className: "", iconSize: [0, 0], html: `<div class="harbour-label">${name.replace(/[<>&]/g, "")}</div>` });

const STYLE = {
  port_area: { color: "#90B849", weight: 1.5, dashArray: "6 4", fillColor: "#90B849", fillOpacity: 0.08 },
  terminal_area: { color: "#F59E0B", weight: 1.5, fillColor: "#F59E0B", fillOpacity: 0.16 },
  dock: { color: "#38BDF8", weight: 1.2, fillColor: "#38BDF8", fillOpacity: 0.12 },
  quay: { color: "#F8FAFC", weight: 3.5, opacity: 0.9, fillOpacity: 0.25 },
  pier: { color: "#E2E8F0", weight: 3, opacity: 0.85, fillOpacity: 0.25 },
  breakwater: { color: "#94A3B8", weight: 3, opacity: 0.8 },
};
const LABEL = { port_area: "Port area", terminal_area: "Terminal", dock: "Dock / basin", quay: "Quay", pier: "Pier / jetty", breakwater: "Breakwater", berth: "Berth / mooring", ferry_terminal: "Ferry terminal" };

function Tip({ f }) {
  return <Tooltip sticky><strong>{f.name || LABEL[f.kind]}</strong><br />{LABEL[f.kind]}{f.operator ? ` · ${f.operator}` : ""}<br /><span style={{ opacity: 0.7 }}>OpenStreetMap {f.osm}</span></Tooltip>;
}

export function HarbourLayer({ ports }) {
  const seen = new Set();
  const feats = [];
  (ports || []).forEach((p) => p.features.forEach((f) => { if (!seen.has(f.osm)) { seen.add(f.osm); feats.push(f); } }));
  return feats.map((f) => {
    if (f.geom === "point") {
      const ferry = f.kind === "ferry_terminal";
      return [
        <CircleMarker key={f.osm} center={f.coords[0]} radius={ferry ? 3 : 5} pathOptions={{ color: ferry ? "#38BDF8" : "#F59E0B", weight: 1.5, fillOpacity: 0.8 }}><Tip f={f} /></CircleMarker>,
        f.name && (f.kind === "port_area" || f.kind === "terminal_area") && <Marker key={`${f.osm}-l`} position={[f.coords[0][0] - 0.0012, f.coords[0][1]]} icon={labelIcon(f.name)} interactive={false} />,
      ];
    }
    const st = STYLE[f.kind] || STYLE.pier;
    if (f.geom === "polygon") return [
      <Polygon key={f.osm} positions={f.coords} pathOptions={st}><Tip f={f} /></Polygon>,
      f.name && (f.kind === "terminal_area" || f.kind === "port_area") && <Marker key={`${f.osm}-l`} position={centroid(f.coords)} icon={labelIcon(f.name)} interactive={false} />,
    ];
    return <Polyline key={f.osm} positions={f.coords} pathOptions={st}><Tip f={f} /></Polyline>;
  });
}

export function PortLayout({ port }) {
  if (!port) return null;
  const c = port.counts;
  const mapped = Object.entries(c).filter(([, n]) => n > 0);
  return (
    <div data-testid="port-layout" className="space-y-3 text-xs">
      <div>
        <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-slate-500">Terminal operators</div>
        {port.operators.length ? <ul className="space-y-1" data-testid="port-operators">{port.operators.map((o) => <li key={o} className="rounded border border-white/5 px-2 py-1 text-slate-200">{o}</li>)}</ul> : <div className="text-slate-500">No terminal concessions listed</div>}
      </div>
      <div>
        <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-slate-500">Mapped in OpenStreetMap</div>
        {mapped.length ? <div className="flex flex-wrap gap-1.5" data-testid="port-osm-counts">{mapped.map(([k, n]) => <span key={k} className="rounded-full border border-white/10 px-2 py-0.5 text-slate-300">{LABEL[k]} <b className="text-slate-100">{n}</b></span>)}</div>
          : <div className="text-amber-300/80" data-testid="port-osm-gap">{port.fetched_at ? "No quays, piers or berths mapped here yet — a data gap, not an empty port" : "Loading map data…"}</div>}
      </div>
    </div>
  );
}
